import React, { useState, useRef, useImperativeHandle } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { Alert, message, Spin } from 'antd';
import useLocales from '../../gql/hooks/useLocales'; 
import FormILT from '../FormILT/FormILT';
import FormBrands from '../FormBrands/FormBrands'; 
import FormLocales from '../FormLocales/FormLocales';
import FormIsFeatured from '../FormIsFeatured/FormIsFeatured';
import FormSaveCancelToolbar from '../FormSaveCancelToolbar/FormSaveCancelToolbar';
import courseFormUtils from '../../pages/CourseEditPage/courseFormUtils';
import useSync from '../../gql/hooks/useSync';
import usePatchCourse from '../../gql/hooks/usePatchCourse';
import useCreateCourse from '../../gql/hooks/useCreateCourse';
import { useFeatureFlags } from '../../../featureFlags';
import { flags } from '../../../featureFlags/flags'; 
import './styles.less';

const TabBasics = ( { form, isSavingCallback, createdCallback, ref } ) => {
    const formILTRef = useRef( null ); 
    const formBrandsRef = useRef( null ); 
    const formLocalesRef = useRef( null );
    const formIsFeaturedRef = useRef( null );
    const [ isEditing, setIsEditing ] = useState( form.getFieldValue( 'id' ) );
    const [ showBrands ] = useFeatureFlags( [ flags.BRANDS ] );
    const [ showILT ] = useFeatureFlags( [ flags.ILT_ENABLED ] );
    const { locales } = useLocales();
    const [ isModified, setIsModified ] = useState( false );
    const [ isSaving, setIsSaving ] = useState( false );
    const [ isLocalesModified, setIsLocalesModified ] = useState( false ); 
    const [ isFeaturedModified, setIsFeaturedModified ] = useState( false );
    const [ isILTModified, setIsILTModified ] = useState( false );
    const [ isBrandsdModified, setIsBrandsdModified ] = useState( false );
    const [ validationErrors, setValidationErrors ] = useState( [] );
    const { saveCourse } = usePatchCourse();
    const { createCourse } = useCreateCourse();
    const { syncData } = useSync();
    const [ openConfirm, setOpenConfirm ] = useState( false );
    const [ messageApi, contextHolder ] = message.useMessage();
  
    useImperativeHandle( ref, () => ( {
        isModified() {
            return isModified;
        },
        showSaveConfirm() {
            setOpenConfirm( true );
        }
    } ) );  
    
    const validateLocales = () => {
        const result = [];

        const getLocaleName = ( code ) => {
            const locale =  locales?.find( i => i.code === code );
            return locale ? locale.label : code;
        };
        
        const validateLocale = ( l ) => {
            if ( !l.name.trim() ) {
                result.push( `A name required for ${ getLocaleName( l.locale ) }` );
            }
        
            if ( !l.description.replace( /(<([^>]+)>)/gi, '' ).replaceAll( '&nbsp;', '' ).trim() ) {
                result.push( `A description required for ${ getLocaleName( l.locale ) }` );
            }
        };

        const newLocales = form.getFieldValue( 'localesForSaving' );
        
        newLocales.forEach( l => validateLocale( l ) );

        // Check if english exists because it is required
        if ( !newLocales.find( i => i.locale === 'en-US' ) ) {
            result.push( 'The language english is required. Please enter a name and description for english' );
        }

        return result;
    };

    const handleLocalesModifiedChange = ( modified ) => {
        setIsLocalesModified( modified );
        setIsModified( modified || isFeaturedModified || isBrandsdModified || isILTModified );
    };
     
    const handleIsFeaturedModifiedChange = ( modified ) => {
        setIsFeaturedModified( modified );
        setIsModified( isLocalesModified || modified || isBrandsdModified || isILTModified );
    };

    const handleIsILTModifiedChange = ( modified ) => {
        setIsILTModified( modified );
        setIsModified( isLocalesModified || modified || isBrandsdModified );
    };

    const handleBrandsModifiedChange = ( modified ) => {
        setIsBrandsdModified( modified );
        setIsModified( isLocalesModified || isFeaturedModified || modified  || isILTModified );
    };

    const handleReset = () => { 
        formILTRef.current.reset();
        formLocalesRef.current.reset();
        formBrandsRef?.current?.reset();
        formIsFeaturedRef.current.reset();
        setValidationErrors( [] );
        setOpenConfirm( false );
        setIsModified( false );
        isSavingCallback( false );
    };

    const validate = () => {
        const localesValid = validateLocales();
        const brandsValid = formBrandsRef?.current ? formBrandsRef.current.validate() : true;

        if ( !brandsValid || localesValid.length > 0 ) {
            setValidationErrors( localesValid );
            return false;
        } else {
            setValidationErrors( [] );
            return true;
        }
    };

    const handleSave = async () => {
        if ( !validate() ) {
            setOpenConfirm( false );
            return;
        }

        setIsSaving( true );
        setOpenConfirm( false );
        isSavingCallback( true );
        let saveError = false;
        let isCreating = false;

        /*****************************
         * Patch Course
         *****************************/
        
        if ( isLocalesModified || isFeaturedModified ) {
            let response;
            
            if ( form.getFieldValue( 'id' ) ) {
                response = await saveCourse( courseFormUtils.getCourseSaveData( form ) );
            } else {
                isCreating = true;
                const courseData = courseFormUtils.getCourseSaveData( form );
                courseData.id = uuidv4();
                response = await createCourse( courseData );
            }

            // Update form data to match what we just saved
            if ( response.success ) { 

                if ( isCreating ) {    
                    form.setFieldsValue( { id: response.response.data.result.id } );
                }

                if ( isLocalesModified ) {
                    form.setFieldsValue( { locales: form.getFieldValue( 'localesForSaving' ) } ); 
                    formLocalesRef.current.reset( false ); 
                }
                    
                if ( isFeaturedModified ) {
                    form.setFieldsValue( { isFeatured: form.getFieldValue( 'isFeaturedModified' ) } ); 
                    formIsFeaturedRef.current.reset(); 
                }
            } else {
                saveError = true;
            }
        } 

        /*****************************
         * Sync Brands
         *****************************/
        
        if ( showBrands && ( isBrandsdModified || ( isCreating && form.getFieldValue( 'brandIdDefault' ) ) ) ) {
            const brandSyncData = courseFormUtils.getBrandSyncValues( form, isCreating );
            
            if ( brandSyncData ) {
                const response = await syncData( brandSyncData );

                if ( response.success ) {
                    form.setFieldsValue( { brandIds: brandSyncData.relatedResourceIds } ); 
                    form.setFieldsValue( { brandIdDefault: undefined } ); 
                    formBrandsRef.current.reset(); 
                } else {
                    saveError = true;
                }
            }
        }

        if ( saveError ) {
            messageApi.error( 'Error saving course!' );
        } else {
            messageApi.success( 'Course saved.' );
        }

        setIsSaving( false );
        isSavingCallback( false );
        
        if ( isCreating ) { 
            createdCallback();
            setIsEditing( true );
        }
    };

    return (
        <div className="basics-tab tab-main-columns">
            
            { contextHolder }

            <div className="left">
                <Spin spinning={ isSaving }>

                    { /* Validatition Errors */ }
                    { validationErrors?.length > 0 && 
                        <Alert 
                            type="error"
                            message="Validation"
                            description={ (
                                <ul>
                                    { validationErrors.map( ( i, index ) => ( 
                                        <li key={ index }>{ i }</li> 
                                    ) ) }
                                </ul>
                            )
                            }
                        />
                    }
                    
                    <FormLocales 
                        ref={ formLocalesRef }
                        form={ form }
                        modifiedCallback={ handleLocalesModifiedChange }
                    />
                    
                    { showBrands &&
                        <FormBrands
                            ref={ formBrandsRef }
                            form={ form }
                            modifiedCallback={ handleBrandsModifiedChange }
                        />
                    }

                    <FormIsFeatured
                        ref={ formIsFeaturedRef }
                        form={ form }
                        modifiedCallback={ handleIsFeaturedModifiedChange }
                    />

                    { showILT && !isEditing &&
                        <FormILT
                            ref={ formILTRef }
                            form={ form }
                            modifiedCallback={ handleIsILTModifiedChange }
                        />
                    }
                </Spin>    
            </div>

            <div className="right">
                <FormSaveCancelToolbar
                    form={ form }
                    open={ openConfirm }
                    saving={ isSaving } 
                    modified={ isModified }
                    saveCallback={ handleSave }
                    cancelCallback={ handleReset }
                />
            </div>
        </div>
    );
};

export default TabBasics;
